import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

function CustomSelect({ options, defaultText, name, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(defaultText);
  
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (value) => {
    setSelectedValue(value);
    setIsOpen(false);
    if (onChange) {
      onChange(value);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.custom-select')) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={`custom-select ${isOpen ? 'active' : ''}`}>
      <button className="select-button" type="button" onClick={toggleDropdown}>
        {selectedValue}
      </button>
      {isOpen && (
        <ul className="select-dropdown">
          {options.map((option, index) => (
            <li key={index}>
              <label>
                <span>{option.label}</span>
                <input
                  type="radio"
                  name={name}
                  value={option.value}
                  onClick={() => handleOptionClick(option.label)}
                />
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function MatchedCourses() {
  const navigate = useNavigate();
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedDuration, setSelectedDuration] = useState('');
  const [courseData, setCourseData] = useState([]);
  const [searchableData, setSearchableData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(10);
  const [isLoading, setIsLoading] = useState(true);

  const [sortOption, setSortOption] = useState('recommendations'); // Added sort option state

  const [selectedState, setSelectedState] = useState(''); // Add this line

  // Add usStates array here
  const usStates = [
    { label: 'Alabama', value: 'AL' },
    { label: 'Alaska', value: 'AK' },
    { label: 'Arizona', value: 'AZ' },
    { label: 'Arkansas', value: 'AR' },
    { label: 'California', value: 'CA' },
    { label: 'Colorado', value: 'CO' },
    { label: 'Connecticut', value: 'CT' },
    { label: 'Delaware', value: 'DE' },
    { label: 'Florida', value: 'FL' },
    { label: 'Georgia', value: 'GA' },
    { label: 'Hawaii', value: 'HI' },
    { label: 'Idaho', value: 'ID' },
    { label: 'Illinois', value: 'IL' },
    { label: 'Indiana', value: 'IN' },
    { label: 'Iowa', value: 'IA' },
    { label: 'Kansas', value: 'KS' },
    { label: 'Kentucky', value: 'KY' },
    { label: 'Louisiana', value: 'LA' },
    { label: 'Maine', value: 'ME' },
    { label: 'Maryland', value: 'MD' },
    { label: 'Massachusetts', value: 'MA' },
    { label: 'Michigan', value: 'MI' },
    { label: 'Minnesota', value: 'MN' },
    { label: 'Mississippi', value: 'MS' },
    { label: 'Missouri', value: 'MO' },
    { label: 'Montana', value: 'MT' },
    { label: 'Nebraska', value: 'NE' },
    { label: 'Nevada', value: 'NV' },
    { label: 'New Hampshire', value: 'NH' },
    { label: 'New Jersey', value: 'NJ' },
    { label: 'New Mexico', value: 'NM' },
    { label: 'New York', value: 'NY' },
    { label: 'North Carolina', value: 'NC' },
    { label: 'North Dakota', value: 'ND' },
    { label: 'Ohio', value: 'OH' },
    { label: 'Oklahoma', value: 'OK' },
    { label: 'Oregon', value: 'OR' },
    { label: 'Pennsylvania', value: 'PA' },
    { label: 'Rhode Island', value: 'RI' },
    { label: 'South Carolina', value: 'SC' },
    { label: 'South Dakota', value: 'SD' },
    { label: 'Tennessee', value: 'TN' },
    { label: 'Texas', value: 'TX' },
    { label: 'Utah', value: 'UT' },
    { label: 'Vermont', value: 'VT' },
    { label: 'Virginia', value: 'VA' },
    { label: 'Washington', value: 'WA' },
    { label: 'West Virginia', value: 'WV' },
    { label: 'Wisconsin', value: 'WI' },
    { label: 'Wyoming', value: 'WY' }
  ];

  const handleSort = (option) => {
    setSortOption(option);
  
    let sortedData = [...searchableData];
    if (option === 'price') {
      sortedData = sortedData.sort((a, b) => {
        const extractPrice = (priceString) => {
          // Extract the numeric part of the price string using a regular expression
          const match = priceString.match(/From\s*\$([0-9,.]+)/);
          return match ? parseFloat(match[1].replace(/,/g, '')) : Infinity;
        };
  
        const priceA = a.price.includes('No cost available') ? Infinity : extractPrice(a.price);
        const priceB = b.price.includes('No cost available') ? Infinity : extractPrice(b.price);
  
        return priceA - priceB;
      });
    }
  
    setCourseData(sortedData.slice(0, visibleCount));
  };
  
  
  
  
  
  const loadData = useCallback(async () => {
    try {
      const universityResponse = await fetch('/data/data.csv');
      const universityText = await universityResponse.text();
      const universityData = parseCSV(universityText);
  
      const costResponse = await fetch('/data/cost.csv');
      const costText = await costResponse.text();
      const costData = parseCostCSV(costText);
  
      const combinedData = universityData.map(course => ({
        ...course,
        price: costData[course.id]
          ? `From $${(Number(costData[course.id]) / 52).toFixed(2)} USD per week`
          : 'No cost available',
      }));
  
      // Add a hardcoded sponsored card
      const sponsoredCard = {
        id: 'sponsored-1',
        title: 'Sponsored Course',
        subtitle: 'Sponsored Location, Sponsored City, SP 12345',
        location: 'https://sponsored-course.com',
        imgSrc: 'https://sponsored-course.com/image.jpg',  // Placeholder image URL
        starSrc: './assets/img/icons/star.svg',
        rating: 5, // Sponsored card gets a perfect rating
        price: 'Special offer!',
        age: 'For all ages',
        detailsPage: 'details-sponsored-page',
        state: 'SP',
        isSponsored: true, // Flag to identify the sponsored card
      };
  
      // Add sponsored card to the beginning of the data
      const finalData = [sponsoredCard, ...combinedData];
  
      setSearchableData(finalData);
      setCourseData(finalData.slice(0, visibleCount));
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setIsLoading(false);
    }
  }, [visibleCount]);
  

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let filteredData = searchableData;
    
    // Apply search query filter
    if (searchQuery) {
      filteredData = filteredData.filter(course =>
        course.title && course.title.toLowerCase().startsWith(searchQuery.toLowerCase())
      );
    }
    
    // Apply state filter
    if (selectedState) {
      filteredData = filteredData.filter(course => 
        course.state === selectedState
      );
    }
    
    setCourseData(filteredData.slice(0, visibleCount));
  }, [searchQuery, searchableData, visibleCount, selectedState]); // Add selectedState to dependencies

  const handleScroll = useCallback(() => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 500) {
      setVisibleCount(prevCount => prevCount + 10);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const parseCSV = (csvText) => {
    const lines = csvText.split('\n');
    const headers = lines[0].split(',').map(header => header.trim());
    return lines.slice(1).map(line => {
      const values = line.split(',').map(value => value.trim().replace(/^"|"$/g, ''));
      const course = {};
      headers.forEach((header, index) => {
        course[header] = values[index];
      });
      
      return {
        id: course.UNITID,
        title: course.INSTNM,
        subtitle: `${course.ADDR}, ${course.CITY}, ${course.STABBR} ${course.ZIP}`,
        location: course.WEBADDR,
        imgSrc: `https://getimage.wordpad.workers.dev/?id=${course.UNITID}&keyword=${course.INSTNM}`,
        starSrc: './assets/img/icons/star.svg',
        rating: 4.5,
        price: 'Loading...',
        age: 'from 14 years',
        detailsPage: 'details-page',
        state: course.STABBR
      };
    });
  };

  const parseCostCSV = (csvText) => {
    const lines = csvText.split('\n');
    const headers = lines[0].split(',').map(header => header.trim());
    const tuitionData = {};

    lines.slice(1).forEach(line => {
      const values = line.split(',').map(value => value.trim().replace(/^"|"$/g, ''));
      const unitIdIndex = headers.indexOf('UNITID');
      const tuitionIndex = headers.indexOf('TUITION2');
      if (unitIdIndex !== -1 && tuitionIndex !== -1) {
        const unitId = values[unitIdIndex];
        const tuition = values[tuitionIndex];
        tuitionData[unitId] = tuition;
      }
    });

    return tuitionData;
  };

  const handleCourseSelection = (course) => {
    console.log("Button clicked", course);
    navigate(`/fetch/${course.id}`);
  };

  return (
    <section className="matched-courses">
      <div className="matched-courses__container container">
        <h2 className="matched-courses__title">Available Programs</h2>

        {/* Search bar */}
        <input
          type="text"
          placeholder="Search by school name"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-bar"
        />

        <div className="matched-courses__body">
          <div className="matched-courses__search courses-search">
            <h2 className="courses-search__caption">Search school</h2>

            {/* Remove this dropdown that was using setSelectedCourse */}
            {/* <CustomSelect
              options={[
                { label: 'Business Certificate Program', value: 'Business Certificate Program' },
                { label: 'Computer Science', value: 'Computer Science' },
                { label: 'Healthcare', value: 'Healthcare' },
                { label: 'Finance', value: 'Finance' },
              ]}
              defaultText="What do you want to learn"
              name="course"
              onChange={setSelectedCourse}
            /> */}

            {/* Keep the state selection dropdown */}
            <CustomSelect
              options={usStates}
              defaultText="Select a state"
              name="state"
              onChange={(label) => setSelectedState(usStates.find(state => state.label === label)?.value || '')}
            />

            <CustomSelect
              options={[
                { label: 'Fort Lauderdale', value: 'Fort Lauderdale' },
                { label: 'Boston', value: 'Boston' },
                { label: 'San Francisco', value: 'San Francisco' },
              ]}
              defaultText="Where do you want to study"
              name="location"
              onChange={setSelectedLocation}
            />

            <div className="courses-search__group">
              <span className="courses-search__group-caption">Calculation of cost</span>
              <CustomSelect
                options={[
                  { label: 'Not sure yet', value: 'Not sure yet' },
                  { label: 'May 2024', value: 'May 2024' },
                  { label: 'June 2024', value: 'June 2024' },
                  { label: 'July 2024', value: 'July 2024' },
                ]}
                defaultText="How long do you want to study?"
                name="duration"
                onChange={setSelectedDuration}
              />
            </div>

            <div className="courses-search__group">
              <span className="courses-search__group-caption">Special features</span>
              <fieldset>
                <label className="courses-search__checkbox">
                  Value for money<input type="checkbox" name="features" value="Value for money" /><span></span>
                </label>
                <label className="courses-search__checkbox">
                  Average age 30+<input type="checkbox" name="features" value="Average age 30+" /><span></span>
                </label>
                <label className="courses-search__checkbox">
                  Located in the city center<input type="checkbox" name="features" value="Located in the city center" /><span></span>
                </label>
              </fieldset>
            </div>
          </div>

          <div className="matched-courses__sort courses-sort">
  <span className="courses-sort__caption">Sort by:</span>
  <ul className="courses-sort__list">
    <li
      className={`courses-sort__item ${sortOption === 'recommendations' ? 'js-active' : ''}`}
      onClick={() => handleSort('recommendations')}
    >
      our recommendations
    </li>
    <li
      className={`courses-sort__item ${sortOption === 'bestReviewed' ? '' : ''}`}
      onClick={() => handleSort('bestReviewed')}
    >
      best reviewed
    </li>
    <li
      className={`courses-sort__item ${sortOption === 'price' ? 'js-active' : ''}`}
      onClick={() => handleSort('price')}
    >
      price
    </li>
  </ul>
</div>


          <div className="matched-courses__cards courses-cards">
            {isLoading ? (
              <div>Loading courses...</div>
            ) : (
              courseData.map(course => (
                <article key={course.id} className="courses-cards-item courses-card">
                  <div className="courses-card__img-wrap">
				<picture>
				  
				  <img src={course.imgSrc} alt="Course Image" />
				</picture>
                  </div>
                  <div className="courses-card__content">
                    <div className="courses-card__head">
                      <h2 className="courses-card__title">{course.title}</h2>
                      <h3 className="courses-card__subtitle">{course.subtitle}</h3>
                    </div>
                    <div className="courses-card__rating rating" data-rating={course.rating}>
                      {[...Array(Math.floor(course.rating))].map((_, index) => (
                        <img key={index} src={course.starSrc} alt="Star" />
                      ))}
                    </div>
                    <span className="courses-card__location">{course.location}</span>
                    <span className="courses-card__price">{course.price}</span>
                    <span className="courses-card__age">{course.age}</span>
                    <button
                      className="button button--primary courses-card__button"
                      onClick={() => handleCourseSelection(course)}
                    >
                      Choose a course
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default MatchedCourses;
